import React, { forwardRef, useRef, useImperativeHandle } from "react";
import { toast } from 'react-toastify';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import ApiClient from "../../../services/api-client"
import ActionsRenderer from "../aggrid-cell-renderers/actions-renderer";
import SimpleObjectRenderer from "../aggrid-cell-renderers/simple-object-renderer";
import CheckboxRenderer from "../aggrid-cell-renderers/checkbox-renderer";
import InvoiceTypeEditor from "../aggrid-cell-editors/invoice-type-editor";
import IncomingClassCategoryTypeEditor from "../aggrid-cell-editors/incoming-classification-category-editor";
import IncomingClassValueTypeEditor from "../aggrid-cell-editors/incoming-classification-value-editor";
import { useEditableGrid } from './useEditableGrid';

const EMPTY_ROW = {
    classificationValue: { value: undefined, label: undefined },
    classificationCategory: { value: undefined, label: undefined }
};

const isValid = (data) =>
    data.classificationCategory && data.classificationCategory.value != undefined && data.classificationCategory.value != "" &&
    data.classificationValue && data.classificationValue.value != undefined && data.classificationValue.value != "";

const ProductCategoryClassificationsGrid = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const toastId = useRef(null);
    const { hasError, setHasError, rowStyle, getRowStyle, initGrid, addRow, checkErrors, getItems, setItems } = useEditableGrid({
        isValid,
        emptyRow: EMPTY_ROW,
        globalRows: true,
    });

    const onGridReady = (params) => {
        initGrid(params, props.productClassifications);
    };

    const onCellValueChanged = (params) => {
        const nodeIndex = params.node.rowIndex;
        const countDisplayedRows = params.api.getDisplayedRowCount() - 1;
        if (nodeIndex == countDisplayedRows && params.newValue != undefined) {
            addRow(params.api);
            params.api.redrawRows();
        }

        const items = [];
        params.api.forEachNode(node => items.push(node));
        items.pop();
        items.forEach(node => {
            if (params.node.data.invoiceType &&
                params.node.data.classificationCategory &&
                params.node.data.classificationValue &&
                node.data.classificationCategory &&
                node.data.classificationValue &&
                node.data.invoiceType && nodeIndex != node.rowIndex) {

                const cond = params.node.data.classificationCategory.value == node.data.classificationCategory.value &&
                    params.node.data.classificationValue.value == node.data.classificationValue.value &&
                    params.node.data.invoiceType.value == node.data.invoiceType.value;

                if (cond) {
                    const row_dom = document.querySelector(`.classificationGrid .ag-center-cols-container >  div[row-index="${params.node.rowIndex}"][role="row"]`);
                    row_dom.style.backgroundColor = '#ffb09c';
                    setTimeout(() => {
                        if (!toast.isActive(toastId.current)) {
                            toastId.current = toast.error(t("duplicateInvoiceTypeCategoryTypeCategoryValue"));
                        }
                        setHasError(true);
                    }, 100);
                    return;
                }
            }
        });

        checkErrors(params.api);
    };

    useImperativeHandle(ref, () => ({
        transformData(data) {
            return data.map(el => {
                const cats = [];
                const values = [];

                el.suggestedInvoiceCategories.forEach(e => {
                    const prefix = e.classificationCategory.replace("CATEGORY_", "") + ") ";
                    cats.push({
                        value: e.classificationCategory,
                        label: prefix + t(e.classificationCategory),
                        classificationValues: e.classificationValues
                    });
                });

                el.classificationValues.forEach(e => {
                    values.push({ value: e, label: t(e) + "(" + e + ")" });
                });

                const prefix = el.classificationCategory.replace("CATEGORY_", "") + ") ";
                return {
                    id: el.id,
                    classificationCategory: { value: el.classificationCategory, label: prefix + t(el.classificationCategory) },
                    classificationValue: { value: el.classificationValue, label: t(el.classificationValue) + "(" + el.classificationValue + ")" },
                    invoiceType: { value: el.invoiceType.id, label: el.invoiceType.name, myDataInvoiceType: el.invoiceType.myDataInvoiceType },
                    classificationCategoryTypes: cats,
                    classificationValues: values,
                    selfPricing: el.selfPricing,
                };
            });
        },
        getData() {
            return getItems().map(element => ({
                id: element.id,
                invoiceType: { id: element.invoiceType.value, myDataInvoiceType: element.invoiceType.myDataInvoiceType, name: element.invoiceType.label },
                classificationCategory: element.classificationCategory.value,
                classificationValue: element.classificationValue.value,
                selfPricing: element.selfPricing
            }));
        },
        hasError() {
            return hasError;
        },
        setData(data) {
            setItems(data);
        }
    }));

    const deleteRowById = (id, api) => {
        let rowData = [];
        api.forEachNode(node => rowData.push(node.data));

        if (id != undefined) {
            ApiClient.deleteProductCategoryClassification(id).then(
                () => {
                    rowData.splice(props.rowIndex, 1);
                    props.api.setRowData(rowData);
                    props.colDef.cellRendererParams.onRemoveRow();
                    toast.success("Delete succeded");
                },
                error => {
                    toast.error("Error: " + error.data);
                    console.error(error.data);
                }
            );
        } else {
            rowData.splice(props.rowIndex, 1);
            props.api.setRowData(rowData);
            props.colDef.cellRendererParams.onRemoveRow();
        }
    };

    return (
        <div className="row">
            <div className="col-md-12">
                <div className="ag-theme-alpine editableGrid classificationGrid" style={{ width: '100%' }}>
                    <AgGridReact
                        onGridReady={onGridReady}
                        rowStyle={rowStyle}
                        getRowStyle={getRowStyle}
                        style={{ width: '100%', height: '300px' }}
                        onCellValueChanged={onCellValueChanged}
                        stopEditingWhenCellsLoseFocus={true}
                        borders={true}
                        defaultColDef={{ resizable: true }}
                        suppressRowTransform={true}
                        singleClickEdit={true}
                        skipHeaderOnAutoSize={true}
                        columnDefs={[
                            { field: "classificationCategoryTypes", hide: true },
                            { field: "classificationValues", hide: true },
                            { field: "invoiceType", flex: 2, minWidth: 100, headerName: t("InvoiceType"), sortable: true, editable: true, options: props.companyInvoiceTypes, cellEditor: InvoiceTypeEditor, cellRenderer: SimpleObjectRenderer },
                            { field: "selfPricing", flex: 1, minWidth: 150, headerName: t("selfPricing"), sortable: true, editable: true, cellRenderer: CheckboxRenderer },
                            { field: "classificationCategory", flex: 3, minWidth: 200, headerName: t("incomingClassificationCategoryType"), headerClass: "required", sortable: true, editable: true, cellRenderer: SimpleObjectRenderer, cellEditor: IncomingClassCategoryTypeEditor },
                            { field: "classificationValue", flex: 2, minWidth: 250, headerName: t("incomingClassificationValueType"), sortable: true, editable: true, cellRenderer: SimpleObjectRenderer, cellEditor: IncomingClassValueTypeEditor },
                            { field: "name", flex: 1, minWidth: 100, headerName: t("Actions"), cellRenderer: ActionsRenderer, cellRendererParams: { deleteRowById: deleteRowById } }
                        ]}
                    >
                    </AgGridReact>
                </div>
            </div>
            <div><br /></div>
        </div>
    );
});
export default ProductCategoryClassificationsGrid;
